import { createSelector } from 'reselect';
import {
    filterAddedAssetsByType,
    filterAvailableAssetsByType,
    getAssetListIntersect
} from './helpers';


const getSites = ({ generalFilter }) => {
    return generalFilter.sites?.data ? generalFilter.sites.data.map((site) => {
        return {
            siteId: site.siteID,
            siteName: site.siteName
        };
    }) : [];
};

const getSitesLoadingStatus = ({ generalFilter }) => {
    return generalFilter.sites?.loadingStatus;
};

const getPowertrainID = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.powertrainID;
};

const getPowertrainName = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.powertrainName;
};

const getSelectedOrganization = ({ generalFilter }) => {
    return generalFilter?.selectedFilters?.organizations[0];
};

const getSelectedSite = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.selectedSite;
};

const getSelectedAssetTypeAvailableSection = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.selectedAssetTypeAvailableSection;
};

const getSelectedAssetTypeAddedSection = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.selectedAssetTypeAddedSection;
};

// eslint-disable-next-line no-unused-vars
const getAvailableAssetsFiltered = createSelector([
    ({ powertrainTools, overview }) => getAssetListIntersect(
        powertrainTools?.wizardV2?.availableAssets?.data,
        overview?.assets?.data
    ),
    ({ powertrainTools }) => powertrainTools.wizardV2.addedAssets,
    ({ powertrainTools }) => powertrainTools.wizardV2.selectedAssetTypeAvailableSection
], (
    assets,
    addedAssets,
    assetType
) => {
    return assets && filterAvailableAssetsByType(assets, addedAssets, assetType);
});

const getAssetFilterAvailableSection = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.assetFilterAvailableSection;
};

const getAddedAssets = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.addedAssets;
};

const getAddedAssetsChangedFlag = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.addedAssetsChangedFlag;
};

const getPowertrainDetailsChangedFlag = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.powertrainDetailsChangedFlag;
};

const getAddedAssetsFiltered = createSelector([
    ({ powertrainTools }) => powertrainTools.wizardV2.addedAssets,
    ({ powertrainTools }) => powertrainTools.wizardV2.selectedAssetTypeAddedSection,
    ({ powertrainTools }) => powertrainTools.wizardV2.assetFilterAddedSection,
], (
    assets,
    assetType,
    searchCriteria,

) => {
    return assets ? filterAddedAssetsByType(assets, assetType, searchCriteria) : [];
});

const getAssetFilterAddedSection = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.assetFilterAddedSection;
};

const getPowertrainWizardStepStates = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.powertrainWizardStepStates;
};

const getPowertrainWizardActiveStep = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.activeStep;
};

const getEditModeFlag = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.isEditMode;
};

const getRowVersion = ({ powertrainTools }) => {
    return powertrainTools.wizardV2.rowVersion;
};

const getAvailableAssetsCurrentPage = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.availableAssets?.pageNumber;
};

const getAvailableAssetsHasMore = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.availableAssets?.hasMore;
};

// Direct selector for raw available assets data without transformation
const getRawAvailableAssets = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.availableAssets?.data || [];
};

const getRawAvailableAssetsLoadingStatus = ({ powertrainTools }) => {
    const loadingStatus = powertrainTools.wizardV2?.availableAssets?.loadingStatus;
    return loadingStatus === 'loading';
};

const getRawAvailableAssetsError = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.availableAssets?.error;
};

const getRawAssetSubTypes = ({ powertrainTools }) => {
    const data = powertrainTools.wizardV2?.assetSubTypes?.data;
    // Ensure we always return an array, even if API returns an object
    if (Array.isArray(data)) {
        return data;
    } else if (data && typeof data === 'object') {
        // Handle the actual API response structure: {subTypes: [{typeConfigId, subTypes: []}]}
        if (data.subTypes && Array.isArray(data.subTypes)) {
            // Extract the subTypes array from the first item (assuming single typeConfigId)
            const firstSubTypeGroup = data.subTypes[0];
            if (firstSubTypeGroup && Array.isArray(firstSubTypeGroup.subTypes)) {
                // Convert string array to object array for consistency
                return firstSubTypeGroup.subTypes.map(subType => ({
                    value: subType,
                    label: subType,
                    name: subType,
                    type: subType
                }));
            }
            return [];
        }
        // Try other common array property names
        else if (data.items && Array.isArray(data.items)) {
            return data.items;
        } else if (data.data && Array.isArray(data.data)) {
            return data.data;
        } else if (data.results && Array.isArray(data.results)) {
            return data.results;
        } else {
            // If it's an object but we can't find an array property, wrap it in an array
            return [data];
        }
    }
    return [];
};

const getRawAssetSubTypesLoadingStatus = ({ powertrainTools }) => {
    const loadingStatus = powertrainTools.wizardV2?.assetSubTypes?.loadingStatus;
    return loadingStatus === 'loading';
};

const getRawAssetSubTypesError = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.assetSubTypes?.error;
};

const getRawAssetDetails = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.assetDetails?.data || [];
};

const getRawAssetDetailsLoadingStatus = ({ powertrainTools }) => {
    const loadingStatus = powertrainTools.wizardV2?.assetDetails?.loadingStatus;
    return loadingStatus === 'loading';
};

const getRawAssetDetailsError = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.assetDetails?.error;
};

// Powertrain operation states
const getIsAddingPowertrain = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.isAddingPowertrain || false;
};

const getIsEditingPowertrain = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.isEditingPowertrain || false;
};

const getPowertrainOperationError = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.powertrainOperationError;
};

const getLastOperationSuccess = ({ powertrainTools }) => {
    return powertrainTools.wizardV2?.lastOperationSuccess;
};

const getSelectedPowertrain = ({ powertrainTools }) => {
    return powertrainTools?.configurationV2?.selectedPowertrain;
};

const getHierarchyData = ({ powertrainTools }) => {
    return powertrainTools?.configurationV2?.hierarchyData?.data?.hierarchyData || null;
};

export const getModel = createSelector(
    [
        getSites,
        getSitesLoadingStatus,
        getPowertrainID,
        getPowertrainName,
        getSelectedOrganization,
        getSelectedSite,
        getSelectedAssetTypeAvailableSection,
        getSelectedAssetTypeAddedSection,
        // Removed getAssets to prevent intersection with overview.assets.data
        // getAssets,
        // getAssetsLoadingStatus,
        getRawAvailableAssets,
        getRawAvailableAssetsLoadingStatus,
        getRawAvailableAssetsError,
        getRawAssetSubTypes,
        getRawAssetSubTypesLoadingStatus,
        getRawAssetSubTypesError,
        getRawAssetDetails,
        getRawAssetDetailsLoadingStatus,
        getRawAssetDetailsError,
        getAssetFilterAvailableSection,
        getAddedAssets,
        getAddedAssetsChangedFlag,
        getPowertrainDetailsChangedFlag,
        getAddedAssetsFiltered,
        getAssetFilterAddedSection,
        getPowertrainWizardStepStates,
        getPowertrainWizardActiveStep,
        getEditModeFlag,
        getRowVersion,
        getAvailableAssetsCurrentPage,
        getAvailableAssetsHasMore,
        getIsAddingPowertrain,
        getIsEditingPowertrain,
        getPowertrainOperationError,
        getLastOperationSuccess,
        getSelectedPowertrain,
        getHierarchyData
    ],
    (
        sites,
        sitesLoadingStatus,
        powertrainID,
        powertrainName,
        selectedOrganization,
        selectedSite,
        selectedAssetTypeAvailableSection,
        selectedAssetTypeAddedSection,
        // Removed assets and assetsLoadingStatus
        availableAssets,
        isLoadingAssets,
        assetsError,
        assetSubTypes,
        isLoadingAssetSubTypes,
        assetSubTypesError,
        assetDetails,
        isLoadingAssetDetails,
        assetDetailsError,
        assetFilterAvailableSection,
        addedAssets,
        addedAssetsChangedFlag,
        powertrainDetailsChangedFlag,
        addedAssetsFiltered,
        assetFilterAddedSection,
        powertrainWizardStepStates,
        activeStep,
        isEditMode,
        rowVersion,
        availableAssetsCurrentPage,
        availableAssetsHasMore,
        isAddingPowertrain,
        isEditingPowertrain,
        powertrainOperationError,
        lastOperationSuccess,
        selectedPowertrain,
        hierarchyData
    ) => {
        return {
            sites,
            sitesLoadingStatus,
            powertrainID,
            powertrainName,
            selectedOrganization,
            selectedSite,
            selectedAssetTypeAvailableSection,
            selectedAssetTypeAddedSection,
            // Removed assets and assetsLoadingStatus to prevent confusion
            availableAssets,
            isLoadingAssets,
            assetsError,
            assetSubTypes,
            isLoadingAssetSubTypes,
            assetSubTypesError,
            assetDetails,
            isLoadingAssetDetails,
            assetDetailsError,
            assetFilterAvailableSection,
            addedAssets,
            addedAssetsChangedFlag,
            powertrainDetailsChangedFlag,
            addedAssetsFiltered,
            assetFilterAddedSection,
            powertrainWizardStepStates,
            activeStep,
            isEditMode,
            rowVersion,
            availableAssetsCurrentPage,
            availableAssetsHasMore,
            isAddingPowertrain,
            isEditingPowertrain,
            powertrainOperationError,
            lastOperationSuccess,
            selectedPowertrain,
            hierarchyData
        };
    }
);
